import React, { useState, useEffect, useCallback, useRef } from 'react';
import './email-designer.scss';
import DataGrid, {
  Column,
  Toolbar,
  Item,
  LoadPanel,
  Sorting,
  HeaderFilter,
  Scrolling,
  SearchPanel,
  DataGridTypes,
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import CheckBox from 'devextreme-react/check-box';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import { useAuth } from '../../contexts/auth';
import {
  getEmailTemplates,
  getEmailTemplateById,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  duplicateEmailTemplate,
  setDefaultEmailTemplate,
} from '../../api/emailTemplates';
import type {
  EmailTemplateDto,
  EmailTemplateDetailDto,
  EmailTemplateSaveRequest,
} from '../../types/email-template';
import { TEMPLATE_TYPE_LABELS } from '../../types/email-template';
import { EmailTemplateEditor } from '../../components/library/email-template-editor/EmailTemplateEditor';
import type { EmailTemplateEditorHandle } from '../../components/library/email-template-editor/EmailTemplateEditor';
import { PlaceholderPanel } from '../../components/library/email-template-editor/PlaceholderPanel';
import { EmailPreview } from '../../components/library/email-template-editor/EmailPreview';

const templateTypeItems = Object.entries(TEMPLATE_TYPE_LABELS).map(([value, text]) => ({
  value,
  text,
}));

export const EmailDesigner = () => {
  const { user } = useAuth();
  const editorRef = useRef<EmailTemplateEditorHandle>(null);

  // ── State ──────────────────────────────────────────────
  const [templates, setTemplates] = useState<EmailTemplateDto[]>([]);
  const [loading, setLoading] = useState(false);

  // Currently editing
  const [editingOid, setEditingOid] = useState<string | null>(null);
  const [editorVisible, setEditorVisible] = useState(false);
  const [formData, setFormData] = useState<{
    Name: string;
    TemplateType: string;
    SubjectLine: string;
    FromName: string;
    FromEmail: string;
    IsDefault: boolean;
  }>({
    Name: '',
    TemplateType: 'MaintenanceReport',
    SubjectLine: '',
    FromName: '',
    FromEmail: '',
    IsDefault: false,
  });
  const [saving, setSaving] = useState(false);

  // Preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // ── Data loading ───────────────────────────────────────
  const loadTemplates = useCallback(async() => {
    setLoading(true);
    try {
      const data = await getEmailTemplates(user?.token);
      setTemplates(data);
    } catch (error: any) {
      notify(error?.response?.data || 'Fout bij laden van templates', 'error', 3000);
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // ── Open editor for new / existing ─────────────────────

  const openNewTemplate = useCallback(() => {
    setEditingOid(null);
    setFormData({
      Name: '',
      TemplateType: 'MaintenanceReport',
      SubjectLine: '',
      FromName: '',
      FromEmail: '',
      IsDefault: false,
    });
    setPreviewHtml('');
    setEditorVisible(true);
    setActiveTab(0);
  }, []);

  const openExistingTemplate = useCallback(
    async(oid: string) => {
      try {
        const detail: EmailTemplateDetailDto = await getEmailTemplateById(oid, user?.token);
        setEditingOid(detail.Oid);
        setFormData({
          Name: detail.Name,
          TemplateType: detail.TemplateType,
          SubjectLine: detail.SubjectLine,
          FromName: detail.FromName,
          FromEmail: detail.FromEmail,
          IsDefault: detail.IsDefault,
        });
        setPreviewHtml(detail.HtmlContent || '');
        setEditorVisible(true);
        setActiveTab(0);

        // Load design once the editor signals ready
        const waitForReady = setInterval(() => {
          if (editorRef.current) {
            try {
              const designObj = detail.DesignJson ? JSON.parse(detail.DesignJson) : {};
              editorRef.current.loadDesign(designObj);
            } catch {
              // design JSON was empty or invalid
            }
            clearInterval(waitForReady);
          }
        }, 500);

        // Safety cleanup
        setTimeout(() => clearInterval(waitForReady), 10000);
      } catch (error: any) {
        notify(error?.response?.data || 'Fout bij laden van template', 'error', 3000);
      }
    },
    [user?.token]
  );

  // ── Save ──────────────────────────────────────────────

  const handleSave = useCallback(async() => {
    if (!formData.Name) {
      notify('Naam is verplicht', 'warning', 2000);
      return;
    }
    setSaving(true);
    try {
      const exported = await editorRef.current?.saveDesign();
      const request: EmailTemplateSaveRequest = {
        Name: formData.Name,
        TemplateType: formData.TemplateType,
        SubjectLine: formData.SubjectLine,
        DesignJson: exported ? JSON.stringify(exported.design) : '',
        HtmlContent: exported?.html || '',
        FromName: formData.FromName,
        FromEmail: formData.FromEmail,
        IsDefault: formData.IsDefault,
      };

      if (editingOid) {
        await updateEmailTemplate(editingOid, request, user?.token);
        notify('Template bijgewerkt', 'success', 2000);
      } else {
        const result = await createEmailTemplate(request, user?.token);
        setEditingOid(result.Oid);
        notify('Template aangemaakt', 'success', 2000);
      }
      await loadTemplates();
    } catch (error: any) {
      const msg = error?.response?.data || 'Fout bij opslaan';
      notify(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error', 3000);
    } finally {
      setSaving(false);
    }
  }, [formData, editingOid, user?.token, loadTemplates]);

  // ── Preview ───────────────────────────────────────────

  const handlePreview = useCallback(async() => {
    const exported = await editorRef.current?.saveDesign();
    setPreviewHtml(exported?.html || '');
    setActiveTab(1);
  }, []);

  // ── Grid actions ──────────────────────────────────────

  const onRowDblClick = useCallback(
    (e: DataGridTypes.RowDblClickEvent) => {
      openExistingTemplate(e.data.Oid);
    },
    [openExistingTemplate]
  );

  const handleDelete = useCallback(
    async(oid: string) => {
      const result = await confirm(
        'Weet je zeker dat je dit template wilt verwijderen?',
        'Template verwijderen'
      );
      if (!result) return;
      try {
        await deleteEmailTemplate(oid, user?.token);
        notify('Template verwijderd', 'success', 2000);
        await loadTemplates();
      } catch (error: any) {
        notify(error?.response?.data || 'Fout bij verwijderen', 'error', 3000);
      }
    },
    [user?.token, loadTemplates]
  );

  const handleDuplicate = useCallback(
    async(oid: string) => {
      try {
        await duplicateEmailTemplate(oid, user?.token);
        notify('Template gedupliceerd', 'success', 2000);
        await loadTemplates();
      } catch (error: any) {
        notify(error?.response?.data || 'Fout bij dupliceren', 'error', 3000);
      }
    },
    [user?.token, loadTemplates]
  );

  const handleSetDefault = useCallback(
    async(oid: string) => {
      try {
        await setDefaultEmailTemplate(oid, user?.token);
        notify('Template ingesteld als standaard', 'success', 2000);
        await loadTemplates();
      } catch (error: any) {
        notify(error?.response?.data || 'Fout bij instellen als standaard', 'error', 3000);
      }
    },
    [user?.token, loadTemplates]
  );

  // ── Cell renderers ────────────────────────────────────

  const typeCellRender = (data: DataGridTypes.ColumnCellTemplateData) => (
    <span>{TEMPLATE_TYPE_LABELS[data.value] || data.value}</span>
  );

  const defaultCellRender = (data: DataGridTypes.ColumnCellTemplateData) => (
    <span className={`default-badge ${data.value ? 'is-default' : ''}`}>
      {data.value ? '★ Standaard' : ''}
    </span>
  );

  const dateCellRender = (data: DataGridTypes.ColumnCellTemplateData) => {
    if (!data.value) return <span>—</span>;
    return <span>{new Date(data.value).toLocaleDateString('nl-NL')}</span>;
  };

  const actionsCellRender = (data: DataGridTypes.ColumnCellTemplateData) => (
    <div className='action-cell'>
      <Button icon='edit' hint='Bewerken' stylingMode='text' onClick={() => openExistingTemplate(data.data.Oid)} />
      <Button icon='copy' hint='Dupliceren' stylingMode='text' onClick={() => handleDuplicate(data.data.Oid)} />
      <Button icon='favorites' hint='Standaard' stylingMode='text' onClick={() => handleSetDefault(data.data.Oid)} />
      <Button icon='trash' hint='Verwijderen' stylingMode='text' onClick={() => handleDelete(data.data.Oid)} />
    </div>
  );

  // ── Render ─────────────────────────────────────────────

  return (
    <div className={`view email-designer ${editorVisible ? 'editor-mode' : ''}`}>
      <div className='view-wrapper'>
        {!editorVisible ? (
          /* ── Template list ─────────────────── */
          <DataGrid
            className='grid'
            noDataText='Geen e-mail templates gevonden'
            focusedRowEnabled
            height='100%'
            dataSource={templates}
            keyExpr='Oid'
            onRowDblClick={onRowDblClick}
            allowColumnReordering
          >
            <LoadPanel enabled={loading} />
            <SearchPanel width={240} visible placeholder='Template zoeken...' />
            <HeaderFilter visible />
            <Sorting mode='multiple' />
            <Scrolling mode='virtual' />
            <Toolbar>
              <Item location='before'>
                <div className='grid-header'>E-mail Templates</div>
              </Item>
              <Item location='after' locateInMenu='auto'>
                <Button
                  icon='plus'
                  text='Nieuw Template'
                  type='default'
                  stylingMode='contained'
                  onClick={openNewTemplate}
                />
              </Item>
              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button icon='refresh' text='Vernieuwen' stylingMode='text' onClick={loadTemplates} />
              </Item>
              <Item name='searchPanel' locateInMenu='auto' />
            </Toolbar>
            <Column dataField='Name' caption='Naam' sortOrder='asc' />
            <Column dataField='TemplateType' caption='Type' width={180} cellRender={typeCellRender} />
            <Column dataField='SubjectLine' caption='Onderwerp' />
            <Column dataField='IsDefault' caption='Standaard' width={120} cellRender={defaultCellRender} />
            <Column dataField='UpdatedAt' caption='Laatst gewijzigd' width={140} cellRender={dateCellRender} />
            <Column caption='Acties' width={180} cellRender={actionsCellRender} allowSorting={false} allowFiltering={false} />
          </DataGrid>
        ) : (
          /* ── Editor view ─────────────────── */
          <div className='editor-view'>
            {/* Top bar */}
            <div className='editor-topbar'>
              <Button
                icon='back'
                text='Terug naar lijst'
                stylingMode='text'
                onClick={() => { setEditorVisible(false); setPreviewHtml(''); }}
              />
              <div className='topbar-title'>
                {editingOid ? 'Template bewerken' : 'Nieuw template'}
              </div>
              <div className='topbar-actions'>
                <Button text='Voorbeeld' icon='eyeopen' stylingMode='outlined' onClick={handlePreview} />
                <Button
                  text={saving ? 'Opslaan...' : 'Opslaan'}
                  icon='save'
                  type='default'
                  stylingMode='contained'
                  disabled={saving}
                  onClick={handleSave}
                />
              </div>
            </div>

            {/* Settings bar */}
            <div className='editor-settings'>
              <TextBox
                label='Naam'
                labelMode='floating'
                value={formData.Name}
                onValueChanged={(e) => setFormData((prev) => ({ ...prev, Name: e.value }))}
                width={220}
              />
              <SelectBox
                label='Type'
                labelMode='floating'
                items={templateTypeItems}
                valueExpr='value'
                displayExpr='text'
                value={formData.TemplateType}
                onValueChanged={(e) => setFormData((prev) => ({ ...prev, TemplateType: e.value }))}
                width={200}
              />
              <TextBox
                label='Onderwerp'
                labelMode='floating'
                value={formData.SubjectLine}
                onValueChanged={(e) => setFormData((prev) => ({ ...prev, SubjectLine: e.value }))}
                width={300}
              />
              <TextBox
                label='Afzender naam'
                labelMode='floating'
                value={formData.FromName}
                onValueChanged={(e) => setFormData((prev) => ({ ...prev, FromName: e.value }))}
                width={180}
              />
              <TextBox
                label='Afzender e-mail'
                labelMode='floating'
                value={formData.FromEmail}
                onValueChanged={(e) => setFormData((prev) => ({ ...prev, FromEmail: e.value }))}
                width={220}
              />
              <CheckBox
                text='Standaard'
                value={formData.IsDefault}
                onValueChanged={(e) => setFormData((prev) => ({ ...prev, IsDefault: e.value }))}
              />
            </div>

            {/* Main editor area with side panel */}
            <div className='editor-main'>
              <div className='editor-content'>
                <TabPanel
                  selectedIndex={activeTab}
                  onSelectedIndexChange={setActiveTab}
                  height='100%'
                  animationEnabled={false}
                >
                  <TabItem title='Ontwerpen'>
                    <div className='editor-tab-content'>
                      <EmailTemplateEditor ref={editorRef} minHeight='100%' />
                    </div>
                  </TabItem>
                  <TabItem title='Voorbeeld'>
                    <div className='editor-tab-content'>
                      <EmailPreview html={previewHtml} subject={formData.SubjectLine} />
                    </div>
                  </TabItem>
                </TabPanel>
              </div>
              <div className='editor-sidebar'>
                <PlaceholderPanel templateType={formData.TemplateType} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
